import { get } from "lodash";
import { getAuthorStories, getCollectionitems, getSearchPageItems, getStories } from "../../../api/utils";
import { format } from "date-fns-tz";
import lowerCase from "lodash/lowerCase";

export const isValidEmail = (email) => {
  const re =
    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  if (!re.test(email)) return false;
  if (email.length > 150 || email.length < 6) return false;
  return true;
};

export const getTime = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) {
    return "Invalid Date";
  }
  const date = new Date(timestamp);
  const options = { timeZone: "Asia/Kolkata", month: "long", day: "numeric", year: "numeric" };
  const datePart = new Intl.DateTimeFormat("en-US", options).format(date);
  const istDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const hours = istDate.getHours().toString().padStart(2, "0");
  const minutes = istDate.getMinutes().toString().padStart(2, "0");
  const timeZone = "IST";

  return `${datePart}, ${hours}:${minutes} ${timeZone}`;
};

export const getTimeForSchema = (timestamp, addDay = false) => {
  if (!timestamp || isNaN(timestamp)) {
    return "Invalid Date";
  }
  const date = new Date(timestamp);
  const options = { timeZone: "Asia/Kolkata", month: "long", day: "numeric", year: "numeric" };
  const istDate = new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  if (addDay) {
    istDate.setDate(istDate.getDate() + addDay);
  }
  const year = istDate.getFullYear().toString().padStart(2, "0");
  const month = istDate.getMonth().toString().padStart(2, "0");
  const day = istDate.getDay().toString().padStart(2, "0");
  const hour = istDate.getHours().toString().padStart(2, "0");
  const minute = istDate.getMinutes().toString().padStart(2, "0");
  const second = istDate.getSeconds().toString().padStart(2, "0");
  const timeZone = "05:30";

  return `${year}-${month}-${day}T${hour}:${minute}:${second}+${timeZone}`;
};

export const getLoadMoreStories = async ({
  offset,
  limit,
  isSearchPage = false,
  slug,
  query,
  shouldUseCollection,
  setStories,
  storiesToRender,
  setStoriesToRender,
  stories,
  isSectionPage,
  authorId,
  setHaveMoreStories,
}) => {
  if (isSearchPage) {
    const loadMoreStories = await getSearchPageItems(slug, offset, limit);
    setStories(stories.slice(0, storiesToRender).concat(loadMoreStories));
    setStoriesToRender(storiesToRender + limit);
    return null;
  }

  if (shouldUseCollection && isSectionPage) {
    const loadMoreStories = await getCollectionitems(slug, offset, limit);
    loadMoreStories.length < limit && setHaveMoreStories(false);
    setStories(stories.slice(0, storiesToRender).concat(loadMoreStories));
    setStoriesToRender(storiesToRender + (limit + 1));
    return null;
  }

  if (authorId) {
    const loadMoreStories = await getAuthorStories(authorId, offset, limit);
    setStories(stories.slice(0, storiesToRender).concat(loadMoreStories));
    setStoriesToRender(storiesToRender + limit);
    return null;
  }

  const loadMoreStories = await getStories(query, slug, offset, limit);
  setStories(stories.slice(0, storiesToRender).concat(loadMoreStories));
  setStoriesToRender(storiesToRender + limit);
  return null;
};

export const getPrimaryVideo = (cards) => {
  const headerVideo = [];
  cards.forEach((card) => {
    card["story-elements"].forEach((element) => {
      if (element.type === "youtube-video") {
        headerVideo.push(element);
      }
    });
  });
  return headerVideo[0];
};

export const hasShowAiSummaryFalse = (story) => {
  const showAiSummary = get(story, ["metadata", "story-attributes", "show_ai_summary"]);

  if (Array.isArray(showAiSummary)) {
    return showAiSummary.includes("false");
  }

  return showAiSummary === "false";
};

export const getVideoStoryCards = (cards, id) => {
  let storyCards = [];
  cards.forEach((card) => {
    const newCard = Object.assign({}, card);
    let elements = [];
    card["story-elements"].forEach((element) => {
      if (element.id !== id) {
        elements = [...elements, element];
      }
    });
    newCard["story-elements"] = elements;
    storyCards = [...storyCards, newCard];
  });
  return storyCards;
};

export const getPublishedDate = (timestamp) => {
  const date = new Date(Number(timestamp));
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const getPublishedTime = (timestamp) => {
  const date = new Date(Number(timestamp));
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const getCategorySubCategory = (slug) => {
  const newSlug = slug?.split("/");
  const sections = newSlug?.slice(0, newSlug.length - 1);
  return {
    category: sections?.[0],
    subCategory: sections?.[1],
  };
};

export const getTemplateName = (name) => {
  const templateMap = {
    "live-blog": "liveblog",
    "book-review": "bookreview",
    photo: "photostory",
    text: "articlestory",
    video: "videostory",
    magazine: "magazinestory",
    sponsored: "sponsoredstory",
    "home-page": "home",
    "tag-page": "listing",
    "search-page": "listing",
    "section-page": "listing",
    "authors-list": "listing",
    "author-page": "authordetail",
    "static-page": "others",
    default: "NA",
  };

  return templateMap[name] || templateMap.default;
};

export const addDatalayerEventStory = (story, templateName = "", categoryName = "") => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "dl_page_info",
    cms_author_name: story?.["assignee-name"],
    byline: story?.authors.map((author) => author.name).toString(),
    category: categoryName || getCategorySubCategory(story?.slug).category || "NA",
    sub_category: getCategorySubCategory(story?.slug).subCategory || "NA",
    content_id: story?.["public-identifier"] || story?.["sequence-no"] || "NA",
    template_name: templateName || getTemplateName(story?.["story-template"]),
    publish_date: getPublishedDate(story?.["published-at"]),
    publish_time: getPublishedTime(story?.["published-at"]),
    login_status: "not-logged-in",
    subscription_status: "not-subscribed", // will be changed
    ad_free: false, // will be changed
    login_wall_enabled: story?.["story-template"] === "magazine",
    platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
  });
};

const getCategorySubCategoryCol = (collection, config) => {
  const parentSection = config?.sections?.find((sec, ind) => sec?.collection?.id === collection.id);
  const parent = config?.sections?.find((sec, ind) => sec?.id === parentSection?.["parent-id"]);
  return {
    category: parent?.slug || parentSection?.slug || "NA",
    subCategory: parent ? parentSection?.slug : "NA",
  };
};

export const addDatalayerEventCollection = (collection, config = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "dl_page_info",
    cms_author_name: "NA",
    byline: "NA",
    category: getCategorySubCategoryCol(collection, config)?.category,
    sub_category: getCategorySubCategoryCol(collection, config)?.subCategory,
    content_id: "NA",
    template_name: collection?.slug === "home" ? "home" : "listing",
    publish_date: "NA",
    publish_time: "NA",
    login_status: "not-logged-in", // will be changed
    subscription_status: "not-subscribed", // will be changed
    ad_free: false, // will be changed
    login_wall_enabled: false,
    platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
  });
};

export const getPlanPrice = (priceCurrency, priceCents) =>
  priceCurrency && priceCents && `${priceCurrency} ${priceCents / 100}`;

export const trackLoadMoreClick = ({ linkCategory, category, subCategory, templateName }) => {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "dl_cta_click",
      link_category: linkCategory,
      link_text: "load more",
      category: category,
      sub_category: subCategory,
      content_id: "NA",
      template_name: templateName,
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
  }
};

export const getLocaleDate = (date, localizedMonths) => {
  const dateTime = new Date(date);
  const formatDate = format(dateTime, "dd MMM yyyy");

  if (!localizedMonths) {
    return formatDate;
  }

  const splitDate = formatDate.split(" ");
  const month = splitDate[1];
  const lowerCaseMonth = lowerCase(month);
  const getLocalizedDate = get(localizedMonths, [lowerCaseMonth]) || month;
  splitDate[1] = getLocalizedDate;
  return splitDate?.join(" ");
};

export const getCardByAttribute = (cards, attributeKey, attributeValue) => {
  return cards.find((card) => get(card, ["metadata", "attributes", attributeKey, 0], null) === attributeValue);
};

export const getCardType = (card) => {
  return get(card, ["metadata", "attributes", "cardtype", 0], null);
};

export const getCardsByAttribute = (cards, attributeKey, attributeValue) => {
  return cards.filter((card) => get(card, ["metadata", "attributes", attributeKey, 0], null) === attributeValue);
};

export const getStoryElementByType = (card, type) => {
  const elements = get(card, ["story-elements"], []);
  return elements.find((element) => get(element, ["type"], null) === type);
};

export const getStoryElementsByType = (card, type) => {
  const elements = get(card, ["story-elements"], []);
  return elements.filter((element) => get(element, ["type"], null) === type);
};

export const getStoryElementsBySubType = (card, type, subtype) => {
  const elements = get(card, ["story-elements"], []);
  return elements.filter(
    (element) => get(element, ["subtype"], null) === subtype && get(element, ["type"], null) === type
  );
};

export const getStoryElementBySubType = (card, type, subtype) => {
  const elements = get(card, ["story-elements"], []);
  return elements.find(
    (element) => get(element, ["subtype"], null) === subtype && get(element, ["type"], null) === type
  );
};

export const removeLeftSlash = (str) => {
  return str.replace(/^\/+/, "");
};

export function withDecodedImageUrls(metaTags) {
  const decodedTags = metaTags.tags.map((tag) => {
    if ((tag.property === "og:image" || tag.name === "twitter:image") && tag.content) {
      return { ...tag, content: decodeURIComponent(tag.content) };
    }
    return tag;
  });
  if (metaTags.constructor && metaTags.constructor.name === "MetaTagList") {
    return new metaTags.constructor(decodedTags);
  }
  return { ...metaTags, tags: decodedTags };
}

/**
 * Get a cookie value by name
 * @param {string} name - The name of the cookie
 * @returns {string|undefined} The cookie value or undefined if not found
 */
export function getCookie(name) {
  if (typeof document === "undefined") return undefined;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}
